import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  ClipboardList, Calendar, BookOpen, ChevronDown, ChevronUp,
  Save, FileText, Users, CheckCircle, Clock, AlertCircle,
  Download, Edit3, Eye, Loader2, BarChart3, XCircle, Circle, Shield, RotateCcw
} from 'lucide-react';
import { toast } from "sonner";
import { getCurrentUser } from '@/data/users';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Use empty string for API base to use relative URLs - works on both preview and production
const API_URL = '';

const STATUS_COLORS = {
  not_started: 'bg-gray-200 text-gray-600',
  in_progress: 'bg-blue-100 text-blue-700',
  submitted: 'bg-yellow-100 text-yellow-700',
  reviewed: 'bg-indigo-100 text-indigo-700',
  approved: 'bg-emerald-100 text-emerald-700',
  not_submitted: 'bg-gray-200 text-gray-600',
  marked: 'bg-emerald-100 text-emerald-700',
};

const STATUS_ICONS = {
  not_started: <Circle className="w-3 h-3" />,
  in_progress: <Clock className="w-3 h-3" />,
  submitted: <FileText className="w-3 h-3" />,
  reviewed: <Eye className="w-3 h-3" />,
  approved: <CheckCircle className="w-3 h-3" />,
  not_submitted: <XCircle className="w-3 h-3" />,
  marked: <CheckCircle className="w-3 h-3" />,
};

export default function IAProgram() {
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedTask, setExpandedTask] = useState(null);
  const [editingDates, setEditingDates] = useState({});
  const [savingDates, setSavingDates] = useState({});
  const [submissions, setSubmissions] = useState({});
  const [loadingSubs, setLoadingSubs] = useState({});
  const [reviewData, setReviewData] = useState({});
  const [savingReview, setSavingReview] = useState({});
  const [resetModalData, setResetModalData] = useState(null);
  const [resetReason, setResetReason] = useState('');
  const [resettingDraft, setResettingDraft] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [progressData, setProgressData] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [activeView, setActiveView] = useState('program'); // 'program' or 'progress'
  const [teacherSubject, setTeacherSubject] = useState(null);
  const [teacherYear, setTeacherYear] = useState(null);
  const [programYear, setProgramYear] = useState(null);
  const [availablePrograms, setAvailablePrograms] = useState([]);
  const [rubricModal, setRubricModal] = useState(null); // { code, description, skill_level, rubric, taskTitle }

  useEffect(() => {
    const detectAndLoad = async () => {
      const user = getCurrentUser();
      let detectedSubject = 'ICT';
      let detectedYear = 13;
      let detectedProgramYear = 2026;

      // Use department as primary detection
      const dept = (user?.department || '').toLowerCase();
      // Also try fetching subjects from timetable API
      let subjectsList = [];
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch('/api/timetable/my-subjects', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          subjectsList = (data.subjects || []).map(s => s.toLowerCase());
        }
      } catch (e) { /* fallback to department only */ }

      const allHints = [dept, ...subjectsList];

      if (allHints.some(s => s.includes('economics') || s.includes('commerce') || s.includes('business'))) {
        detectedSubject = 'Economics';
      } else if (allHints.some(s => s.includes('french') || s.includes('français'))) {
        detectedSubject = 'French';
      } else if (allHints.some(s => s.includes('math') || s.includes('calculus'))) {
        detectedSubject = 'Mathematics';
      } else if (allHints.some(s => s.includes('english') || s.includes('language') || s.includes('literature'))) {
        detectedSubject = 'English';
      } else if (allHints.some(s => s.includes('physics'))) {
        detectedSubject = 'Physics';
      } else if (allHints.some(s => s.includes('computer') || s.includes('ict') || s.includes('technology'))) {
        detectedSubject = 'ICT';
      }

      // Check restricted_to for year level
      const restricted = user?.restrictedTo || user?.restricted_to || '';
      if (restricted.includes('12')) detectedYear = 12;
      if (restricted.includes('13')) detectedYear = 13;

      // Fetch available programs to determine the correct program year for this subject
      try {
        const progRes = await fetch('/api/ia-programs/list/all');
        if (progRes.ok) {
          const progData = await progRes.json();
          const programs = progData.programs || [];
          setAvailablePrograms(programs);
          const matchingProg = programs.find(p => p.subject === detectedSubject && p.year_level === detectedYear);
          if (matchingProg) {
            detectedProgramYear = matchingProg.year;
          }
        }
      } catch (e) { console.error('Error loading programs:', e); }

      setTeacherSubject(detectedSubject);
      setTeacherYear(detectedYear);
      setProgramYear(detectedProgramYear);
      loadProgram(0, detectedSubject, detectedYear, detectedProgramYear);
    };
    detectAndLoad();
  }, []);

  const loadAvailablePrograms = async () => {
    try {
      const res = await fetch('/api/ia-programs/list/all');
      if (res.ok) {
        const data = await res.json();
        setAvailablePrograms(data.programs || []);
      }
    } catch (e) { console.error('Error loading programs list:', e); }
  };

  const switchProgram = (prog) => {
    setTeacherSubject(prog.subject);
    setTeacherYear(prog.year_level);
    setProgramYear(prog.year);
    setLoading(true);
    setProgram(null);
    setExpandedTask(null);
    loadProgram(0, prog.subject, prog.year_level, prog.year);
  };

  const loadProgram = async (retryCount, subject, year, pYear) => {
    const s = subject || teacherSubject || 'ICT';
    const y = year || teacherYear || 13;
    const py = pYear || programYear || 2026;
    try {
      const response = await fetch(`/api/ia-programs/${s}/${y}/${py}`);
      if (response.ok) {
        const data = await response.json();
        setProgram(data);
        if (data.tasks?.length > 0) setExpandedTask(data.tasks[0].id);
        setLoading(false);
      } else if (response.status === 404) {
        setProgram(null);
        setLoading(false);
      } else if (retryCount < 3) {
        setTimeout(() => loadProgram(retryCount + 1), 3000);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error loading IA program:', err);
      if (retryCount < 3) {
        setTimeout(() => loadProgram(retryCount + 1), 3000);
      } else {
        setLoading(false);
      }
    }
  };

  const loadProgress = async () => {
    const user = getCurrentUser();
    if (!user?.school_id) {
      toast.error('School info not found');
      return;
    }
    setProgressLoading(true);
    try {
      const response = await fetch(`/api/ia-programs/progress/${user.school_id}/${teacherSubject || 'ICT'}/${teacherYear || 13}/${programYear || 2026}`);
      if (response.ok) {
        const data = await response.json();
        setProgressData(data);
        setActiveView('progress');
      } else {
        toast.error('Failed to load progress data');
      }
    } catch (err) {
      console.error('Error loading progress:', err);
      toast.error('Failed to load progress');
    } finally {
      setProgressLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const handleDraftDateChange = (draftId, field, value) => {
    setEditingDates(prev => ({
      ...prev,
      [draftId]: { ...(prev[draftId] || {}), [field]: value }
    }));
  };

  const saveDraftDates = async (draftId) => {
    const dates = editingDates[draftId];
    if (!dates) return;
    setSavingDates(prev => ({ ...prev, [draftId]: true }));
    try {
      const response = await fetch(`/api/ia-programs/drafts/${draftId}/dates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dates),
      });
      if (response.ok) {
        toast.success('Dates updated');
        setEditingDates(prev => { const n = { ...prev }; delete n[draftId]; return n; });
        loadProgram();
      }
    } catch { toast.error('Failed to update dates'); }
    finally { setSavingDates(prev => ({ ...prev, [draftId]: false })); }
  };

  const saveTaskDates = async (taskId, dates) => {
    setSavingDates(prev => ({ ...prev, [taskId]: true }));
    try {
      const response = await fetch(`/api/ia-programs/tasks/${taskId}/dates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dates),
      });
      if (response.ok) {
        toast.success('Task dates updated');
        setEditingDates(prev => { const n = { ...prev }; delete n[taskId]; return n; });
        loadProgram();
      }
    } catch { toast.error('Failed to update dates'); }
    finally { setSavingDates(prev => ({ ...prev, [taskId]: false })); }
  };

  const loadSubmissions = async (taskId) => {
    setLoadingSubs(prev => ({ ...prev, [taskId]: true }));
    try {
      const response = await fetch(`/api/ia-programs/tasks/${taskId}/submissions`);
      if (response.ok) {
        const data = await response.json();
        setSubmissions(prev => ({ ...prev, [taskId]: data }));
      }
    } catch { toast.error('Failed to load submissions'); }
    finally { setLoadingSubs(prev => ({ ...prev, [taskId]: false })); }
  };

  const submitReview = async (submissionId, draftId) => {
    const data = reviewData[submissionId];
    if (!data?.feedback) { toast.error('Please enter feedback'); return; }
    setSavingReview(prev => ({ ...prev, [submissionId]: true }));
    try {
      const response = await fetch(`/api/ia-programs/submissions/${submissionId}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feedback: data.feedback,
          status: data.status || 'reviewed',
          score: data.score ? parseInt(data.score) : null,
        }),
      });
      if (response.ok) {
        toast.success('Review submitted');
        setReviewData(prev => { const n = { ...prev }; delete n[submissionId]; return n; });
        // Reload the task's submissions
        const taskId = Object.keys(submissions).find(tid =>
          submissions[tid]?.drafts?.some(d => d.submissions?.some(s => s.id === submissionId))
        );
        if (taskId) loadSubmissions(taskId);
      }
    } catch { toast.error('Failed to submit review'); }
    finally { setSavingReview(prev => ({ ...prev, [submissionId]: false })); }
  };

  const handleResetDraft = async () => {
    if (!resetModalData) return;
    setResettingDraft(true);
    try {
      const response = await fetch(`/api/ia-programs/drafts/${resetModalData.draftId}/reset/${resetModalData.studentId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: resetReason }),
      });
      if (response.ok) {
        toast.success(`Draft reset for ${resetModalData.studentName}. Student can now resubmit.`);
        // Reload submissions for this task
        if (resetModalData.taskId) loadSubmissions(resetModalData.taskId);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to reset draft');
      }
    } catch { toast.error('Failed to reset draft'); }
    finally {
      setResettingDraft(false);
      setResetModalData(null);
      setResetReason('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="ia-loading">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        <span className="ml-3 text-lg">Loading IA Program...</span>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="text-center py-20" data-testid="ia-not-found">
        <ClipboardList className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-600">No IA Program Found</h2>
        <p className="text-gray-400 mt-2">No Internal Assessment program has been set up yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="ia-program-page">
      {/* Program Selector - only show programs relevant to this teacher */}
      {availablePrograms.filter(p => p.subject === teacherSubject).length > 1 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="ia-program-selector">
          <span className="text-sm text-gray-500 font-medium">IA Programs:</span>
          {availablePrograms.filter(p => p.subject === teacherSubject).map(p => (
            <Button
              key={p.id}
              size="sm"
              variant={teacherSubject === p.subject && programYear === p.year ? 'default' : 'outline'}
              className={teacherSubject === p.subject && programYear === p.year
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'hover:bg-emerald-50 border-emerald-200'}
              onClick={() => switchProgram(p)}
              data-testid={`program-btn-${p.subject.toLowerCase()}`}
            >
              {p.subject} Y{p.year_level} ({p.year})
            </Button>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-700 to-teal-600 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold tracking-tight" data-testid="ia-program-title">{program.title}</h1>
            <p className="text-emerald-100 mt-1 text-sm">
              {program.subject} - Year {program.year_level} | Total IA Weighting: {program.total_weighting}%
            </p>
          </div>
          <div className="flex items-center gap-3 ml-4 shrink-0">
            <a href={`/api/ia-programs/generate-pdf/${program.subject}/${program.year_level}/${program.year}`} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 bg-white text-emerald-700 font-medium rounded-lg px-4 py-2.5 text-sm shadow-sm hover:bg-emerald-50 transition-all"
               data-testid="download-pdf-btn">
              <Download className="w-4 h-4" />
              Download PDF
            </a>
            <a href={`/api/ia-programs/generate-docx/${program.subject}/${program.year_level}/${program.year}`} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white font-medium rounded-lg px-4 py-2.5 text-sm border border-white/30 hover:bg-white/25 transition-all"
               data-testid="download-word-btn">
              <FileText className="w-4 h-4" />
              Download Word
            </a>
            {program.subject === 'ICT' && (
              <a href="/api/grades/gradebook/ict-2026/word" target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2.5 text-sm shadow-sm hover:bg-amber-300 transition-all"
                 data-testid="download-gradebook-btn"
                 title="Download the principal's Continuous Assessment Marks form filled with Y12 ICT & Y13 ICT students">
                <FileText className="w-4 h-4" />
                Grade Book (Y12 + Y13)
              </a>
            )}
          </div>
        </div>
        
        {/* Task overview badges */}
        <div className="flex flex-wrap gap-3 mt-5">
          {program.tasks.map(task => (
            <div key={task.id} className="bg-white/15 backdrop-blur-sm rounded-lg px-3 py-2 text-sm border border-white/10">
              <span className="font-semibold">Task {task.task_number}:</span> {task.title} ({task.weighting}%)
            </div>
          ))}
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/20">
          <Button
            size="sm"
            variant={activeView === 'program' ? 'default' : 'ghost'}
            className={activeView === 'program' ? 'bg-white text-emerald-700 hover:bg-white/90' : 'text-white hover:bg-white/20'}
            onClick={() => setActiveView('program')}
            data-testid="view-program-btn"
          >
            <ClipboardList className="w-4 h-4 mr-1" /> Program & Dates
          </Button>
          <Button
            size="sm"
            variant={activeView === 'progress' ? 'default' : 'ghost'}
            className={activeView === 'progress' ? 'bg-white text-emerald-700 hover:bg-white/90' : 'text-white hover:bg-white/20'}
            onClick={() => { if (!progressData) loadProgress(); else setActiveView('progress'); }}
            disabled={progressLoading}
            data-testid="view-progress-btn"
          >
            {progressLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <BarChart3 className="w-4 h-4 mr-1" />}
            Student Progress
          </Button>
        </div>
      </div>

      {/* Progress Dashboard View */}
      {activeView === 'progress' && progressData && (
        <ProgressDashboard data={progressData} tasks={program.tasks} />
      )}

      {/* Tasks - Program View */}
      {activeView === 'program' && program.tasks.map(task => {
        const isExpanded = expandedTask === task.id;
        const daysUntilFinal = getDaysUntil(task.final_submission_date);
        
        return (
          <Card key={task.id} className="border-l-4 border-l-emerald-500 shadow-sm" data-testid={`task-${task.task_number}`}>
            <CardHeader
              className="cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setExpandedTask(isExpanded ? null : task.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">
                    {task.task_number}
                  </div>
                  <div>
                    <CardTitle className="text-lg" data-testid={`task-${task.task_number}-title`}>
                      Task {task.task_number}: {task.title}
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-0.5">{task.theme} | {task.weighting}% weighting</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {daysUntilFinal !== null && (
                    <Badge variant={daysUntilFinal < 0 ? "destructive" : daysUntilFinal < 14 ? "warning" : "secondary"}>
                      {daysUntilFinal < 0 ? 'Overdue' : `${daysUntilFinal} days left`}
                    </Badge>
                  )}
                  <Badge variant="outline">{task.drafts.length} drafts</Badge>
                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </div>
            </CardHeader>

            {isExpanded && (
              <CardContent className="space-y-5">
                <p className="text-gray-600">{task.description}</p>

                {/* Task-Level Dates */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="text-xs text-blue-600 font-medium flex items-center gap-1"><Calendar className="w-3 h-3" /> Start</div>
                    <div className="text-sm font-semibold mt-1">{formatDate(task.start_date)}</div>
                    <Input type="date" className="mt-1 h-7 text-xs" defaultValue={task.start_date}
                      onChange={e => handleDraftDateChange(task.id, 'start_date', e.target.value)} />
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3">
                    <div className="text-xs text-orange-600 font-medium flex items-center gap-1"><Calendar className="w-3 h-3" /> End</div>
                    <div className="text-sm font-semibold mt-1">{formatDate(task.end_date)}</div>
                    <Input type="date" className="mt-1 h-7 text-xs" defaultValue={task.end_date}
                      onChange={e => handleDraftDateChange(task.id, 'end_date', e.target.value)} />
                  </div>
                  <div className="bg-red-50 rounded-lg p-3">
                    <div className="text-xs text-red-600 font-medium flex items-center gap-1"><Calendar className="w-3 h-3" /> Final Due</div>
                    <div className="text-sm font-semibold mt-1">{formatDate(task.final_submission_date)}</div>
                    <Input type="date" className="mt-1 h-7 text-xs" defaultValue={task.final_submission_date}
                      onChange={e => handleDraftDateChange(task.id, 'final_submission_date', e.target.value)} />
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <div className="text-xs text-purple-600 font-medium flex items-center gap-1"><Calendar className="w-3 h-3" /> Results to EAU</div>
                    <div className="text-sm font-semibold mt-1">{formatDate(task.results_to_eau_date)}</div>
                    <Input type="date" className="mt-1 h-7 text-xs" defaultValue={task.results_to_eau_date}
                      onChange={e => handleDraftDateChange(task.id, 'results_to_eau_date', e.target.value)} />
                  </div>
                </div>
                {editingDates[task.id] && (
                  <Button size="sm" onClick={() => saveTaskDates(task.id, editingDates[task.id])}
                    disabled={savingDates[task.id]} data-testid={`save-task-dates-${task.task_number}`}>
                    {savingDates[task.id] ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    Save Task Dates
                  </Button>
                )}

                {/* Drafts */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Drafts & Timeline
                  </h3>
                  <div className="space-y-3">
                    {task.drafts.map(draft => {
                      const daysLeft = getDaysUntil(draft.end_date);
                      return (
                        <div key={draft.id} className="border rounded-lg p-4 bg-white hover:shadow-sm transition-shadow"
                          data-testid={`draft-${task.task_number}-${draft.draft_number}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">Draft {draft.draft_number}</Badge>
                                <span className="font-medium text-sm">{draft.title}</span>
                              </div>
                              <p className="text-sm text-gray-500 mt-1">{draft.description}</p>
                              {draft.learning_outcomes_covered?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  <TooltipProvider delayDuration={150}>
                                    {draft.learning_outcomes_covered.map((lo, idx) => {
                                      // Handle both object format {slo_code: "..."} and string format
                                      const code = typeof lo === 'object' ? lo.slo_code : lo;
                                      const full = task.learning_outcomes?.find(x => x.slo_code === code);
                                      const openRubric = () => setRubricModal({
                                        code,
                                        description: full?.description,
                                        skill_level: full?.skill_level,
                                        rubric: task.scoring_rubric,
                                        taskTitle: `Task ${task.task_number}: ${task.title}`,
                                      });
                                      return (
                                        <Tooltip key={code || idx}>
                                          <TooltipTrigger asChild>
                                            <Badge
                                              variant="secondary"
                                              role="button"
                                              tabIndex={0}
                                              onClick={openRubric}
                                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openRubric(); } }}
                                              className="text-xs cursor-pointer hover:bg-emerald-100 hover:text-emerald-800 transition-colors"
                                              data-testid={`slo-badge-${code}`}
                                            >
                                              {code}
                                            </Badge>
                                          </TooltipTrigger>
                                          <TooltipContent side="top" className="max-w-xs">
                                            <div className="space-y-1.5">
                                              <div className="flex items-center gap-2">
                                                <code className="text-[10px] font-mono text-emerald-300">{code}</code>
                                                {full?.skill_level !== undefined && (
                                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/15">
                                                    Skill Level {full.skill_level}
                                                  </span>
                                                )}
                                              </div>
                                              <p className="text-xs leading-snug">
                                                {full?.description || 'Full description not available — see Learning Outcomes section below.'}
                                              </p>
                                              <p className="text-[10px] text-emerald-300 pt-0.5 border-t border-white/10 mt-1">
                                                Click badge to view scoring rubric →
                                              </p>
                                            </div>
                                          </TooltipContent>
                                        </Tooltip>
                                      );
                                    })}
                                  </TooltipProvider>
                                </div>
                              )}
                            </div>
                            <div className="text-right ml-4 flex-shrink-0">
                              {daysLeft !== null && (
                                <Badge variant={daysLeft < 0 ? "destructive" : daysLeft < 7 ? "warning" : "secondary"} className="text-xs">
                                  {daysLeft < 0 ? 'Past due' : `${daysLeft}d left`}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">Start:</span>
                              <Input type="date" className="h-7 text-xs flex-1"
                                defaultValue={draft.start_date}
                                onChange={e => handleDraftDateChange(draft.id, 'start_date', e.target.value)} />
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">End:</span>
                              <Input type="date" className="h-7 text-xs flex-1"
                                defaultValue={draft.end_date}
                                onChange={e => handleDraftDateChange(draft.id, 'end_date', e.target.value)} />
                            </div>
                          </div>
                          {editingDates[draft.id] && (
                            <Button size="sm" variant="outline" className="mt-2" onClick={() => saveDraftDates(draft.id)}
                              disabled={savingDates[draft.id]} data-testid={`save-draft-dates-${draft.draft_number}`}>
                              {savingDates[draft.id] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                              Save
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Learning Outcomes */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" /> Learning Outcomes
                  </h3>
                  <div className="space-y-2">
                    {task.learning_outcomes.map((lo, idx) => (
                      <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                        <Badge variant="secondary" className="text-xs flex-shrink-0 mt-0.5">
                          Skill {lo.skill_level}
                        </Badge>
                        <div>
                          <code className="text-xs text-emerald-600">{lo.slo_code}</code>
                          <p className="text-sm mt-0.5">{lo.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Task Outline */}
                {task.task_outline && (
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <ClipboardList className="w-4 h-4" /> What Students Need To Do
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 text-sm whitespace-pre-line">{task.task_outline}</div>
                  </div>
                )}

                {/* Student Submissions */}
                <div>
                  <Button 
                    variant={submissions[task.id] ? "default" : "outline"} 
                    onClick={() => loadSubmissions(task.id)}
                    disabled={loadingSubs[task.id]} 
                    className={submissions[task.id] ? "bg-indigo-600 hover:bg-indigo-700 text-white" : "border-indigo-300 text-indigo-700 hover:bg-indigo-50"}
                    data-testid={`load-submissions-${task.task_number}`}>
                    {loadingSubs[task.id] ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}
                    {submissions[task.id] ? 'Refresh Submissions' : 'View Student Submissions'}
                  </Button>

                  {submissions[task.id] && (
                    <div className="mt-5 space-y-5">
                      {submissions[task.id].drafts.map((draftSub, dIdx) => (
                        <div key={draftSub.draft_id} className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                          {/* Draft Header */}
                          <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-4 py-2.5 flex items-center justify-between">
                            <h4 className="font-semibold text-sm text-white flex items-center gap-2">
                              <FileText className="w-3.5 h-3.5 text-slate-300" />
                              {draftSub.draft_title}
                            </h4>
                            <Badge variant="outline" className="text-xs border-slate-400 text-slate-200">
                              {draftSub.submissions.length} submission{draftSub.submissions.length !== 1 ? 's' : ''}
                            </Badge>
                          </div>

                          {/* Submissions List */}
                          <div className="divide-y divide-slate-100">
                            {draftSub.submissions.length === 0 ? (
                              <div className="p-4 text-center">
                                <p className="text-sm text-gray-400 italic">No submissions yet for this draft</p>
                              </div>
                            ) : (
                              draftSub.submissions.map(sub => (
                                <div key={sub.id} className="p-4 hover:bg-slate-50/50 transition-colors space-y-3">
                                  {/* Student Header Row */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                                        sub.status === 'approved' ? 'bg-emerald-500' :
                                        sub.status === 'reviewed' ? 'bg-blue-500' :
                                        'bg-slate-400'
                                      }`}>
                                        {sub.student_name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                      </div>
                                      <div>
                                        <span className="font-semibold text-sm text-slate-800">{sub.student_name}</span>
                                        {sub.submitted_at && (
                                          <p className="text-xs text-slate-400">{new Date(sub.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge className={`text-xs ${
                                        sub.status === 'approved' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                                        sub.status === 'reviewed' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                                        'bg-orange-100 text-orange-700 border-orange-200'
                                      }`} variant="outline">
                                        {sub.status === 'approved' ? 'Approved' : sub.status === 'reviewed' ? 'Reviewed' : 'Pending'}
                                      </Badge>
                                      {sub.score !== null && sub.score !== undefined && (
                                        <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-xs" variant="outline">
                                          Score: {sub.score}
                                        </Badge>
                                      )}
                                      <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                                        data-testid={`reset-draft-${sub.id}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setResetModalData({
                                            draftId: draftSub.draft_id,
                                            studentId: sub.student_id,
                                            studentName: sub.student_name,
                                            draftTitle: draftSub.draft_title,
                                            taskId: Object.keys(submissions).find(tid =>
                                              submissions[tid]?.drafts?.some(d => d.draft_id === draftSub.draft_id))
                                          });
                                        }}>
                                        <RotateCcw className="w-3.5 h-3.5" />
                                      </Button>
                                    </div>
                                  </div>

                                  {/* Student Content */}
                                  {sub.content && (
                                    <div className="bg-white border border-slate-200 rounded-lg p-3 text-sm text-slate-700 leading-relaxed">
                                      {sub.content}
                                    </div>
                                  )}

                                  {/* Files */}
                                  {sub.files?.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                      {sub.files.map(fname => (
                                        <a key={fname} href={`/api/ia-programs/files/${fname}`}
                                          target="_blank" rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 text-xs text-blue-700 hover:bg-blue-100 transition-colors">
                                          <FileText className="w-3.5 h-3.5" />
                                          <span className="max-w-[150px] truncate">{fname.split('_').pop()}</span>
                                          <Download className="w-3 h-3" />
                                        </a>
                                      ))}
                                    </div>
                                  )}

                                  {/* Anti-Cheat Alert */}
                                  {(sub.paste_count > 0 || sub.anti_cheat_flags?.length > 0) && (
                                    <div className="bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid={`anti-cheat-draft-${sub.id}`}>
                                      <div className="flex items-center gap-1.5 text-amber-800 text-xs font-bold mb-1">
                                        <AlertCircle className="w-4 h-4" />
                                        Anti-Cheat Alert
                                      </div>
                                      {sub.paste_count > 0 && (
                                        <p className="text-xs text-amber-700">Copy-paste detected: {sub.paste_count} time(s)</p>
                                      )}
                                      {sub.anti_cheat_flags?.map((flag, i) => (
                                        <p key={i} className="text-xs text-amber-700">{flag}</p>
                                      ))}
                                    </div>
                                  )}

                                  {/* Originality Report */}
                                  {sub.originality_score !== null && sub.originality_score !== undefined && (
                                    <div className={`rounded-lg p-3 border text-xs ${
                                      sub.originality_score >= 70 ? 'bg-emerald-50 border-emerald-300' :
                                      sub.originality_score >= 40 ? 'bg-amber-50 border-amber-300' :
                                      'bg-red-50 border-red-300'
                                    }`} data-testid={`originality-report-${sub.id}`}>
                                      <div className="flex items-center justify-between mb-1.5">
                                        <span className="font-bold flex items-center gap-1.5">
                                          <Shield className="w-4 h-4" />
                                          Originality Report
                                        </span>
                                        <div className="flex items-center gap-2">
                                          <Badge variant="outline" className={`text-xs font-bold ${
                                            sub.originality_score >= 70 ? 'border-emerald-500 text-emerald-700 bg-emerald-100' :
                                            sub.originality_score >= 40 ? 'border-amber-500 text-amber-700 bg-amber-100' :
                                            'border-red-500 text-red-700 bg-red-100'
                                          }`}>
                                            {sub.originality_score}% Original
                                          </Badge>
                                          {sub.ai_likelihood && sub.ai_likelihood !== 'low' && (
                                            <Badge className="text-xs bg-red-500 text-white">
                                              AI: {sub.ai_likelihood}
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                      {sub.originality_report?.summary && (
                                        <p className="text-gray-600 mb-1.5">{sub.originality_report.summary}</p>
                                      )}
                                      {sub.originality_report?.flagged_phrases?.length > 0 && (
                                        <div className="mt-1.5 space-y-1 bg-white/60 rounded p-2">
                                          {sub.originality_report.flagged_phrases.slice(0, 3).map((fp, i) => (
                                            <div key={i} className="text-gray-500">
                                              <span className="italic text-red-600">&ldquo;{fp.phrase?.substring(0, 60)}&rdquo;</span> — {fp.reason}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Teacher Feedback Display */}
                                  {sub.teacher_feedback && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                                      <span className="font-semibold text-blue-700">Teacher Feedback:</span>
                                      <span className="text-blue-600 ml-1">{sub.teacher_feedback}</span>
                                    </div>
                                  )}

                                  {/* Per-SLO Scoring Panel */}
                                  <SloScoringPanel
                                    submissionId={sub.id}
                                    submissionKind="draft"
                                    studentName={sub.student_name}
                                    sloCodes={
                                      (task.drafts?.find(d => d.id === draftSub.draft_id)
                                        ?.learning_outcomes_covered) || []
                                    }
                                    taskLearningOutcomes={task.learning_outcomes || []}
                                    rubric={task.scoring_rubric || []}
                                    taskTitle={`Task ${task.task_number}: ${task.title}`}
                                  />

                                  {/* Review Form */}
                                  {sub.status === 'submitted' && (
                                    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl p-4 space-y-3">
                                      <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Review & Grade</p>
                                      <Textarea placeholder="Write feedback for the student..." className="text-sm bg-white border-indigo-200 focus:border-indigo-400"
                                        value={reviewData[sub.id]?.feedback || ''}
                                        onChange={e => setReviewData(prev => ({
                                          ...prev, [sub.id]: { ...(prev[sub.id] || {}), feedback: e.target.value }
                                        }))} />
                                      <div className="flex items-center gap-3">
                                        <Input type="number" placeholder="Score" className="w-24 h-9 text-sm bg-white border-indigo-200"
                                          value={reviewData[sub.id]?.score || ''}
                                          onChange={e => setReviewData(prev => ({
                                            ...prev, [sub.id]: { ...(prev[sub.id] || {}), score: e.target.value }
                                          }))} />
                                        <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100"
                                          disabled={savingReview[sub.id]}
                                          onClick={() => {
                                            setReviewData(prev => ({ ...prev, [sub.id]: { ...(prev[sub.id] || {}), status: 'reviewed' } }));
                                            submitReview(sub.id, draftSub.draft_id);
                                          }}>
                                          {savingReview[sub.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
                                          Review
                                        </Button>
                                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                          disabled={savingReview[sub.id]}
                                          onClick={() => {
                                            setReviewData(prev => ({ ...prev, [sub.id]: { ...(prev[sub.id] || {}), status: 'approved' } }));
                                            submitReview(sub.id, draftSub.draft_id);
                                          }}>
                                          {savingReview[sub.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
                                          Approve
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}

      {/* SLO Scoring Rubric Modal */}
      <Dialog open={!!rubricModal} onOpenChange={(open) => { if (!open) setRubricModal(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="slo-rubric-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-600" />
              SOLO Scoring Rubric
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 pt-2">
                <div className="text-xs text-gray-500">{rubricModal?.taskTitle}</div>
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">{rubricModal?.code}</code>
                  {rubricModal?.skill_level !== undefined && (
                    <Badge variant="secondary" className="text-xs">
                      Skill Level {rubricModal.skill_level} (max score: {rubricModal.skill_level})
                    </Badge>
                  )}
                </div>
                {rubricModal?.description && (
                  <p className="text-sm text-gray-700 leading-relaxed pt-1">
                    <span className="font-semibold">Learning Outcome: </span>
                    {rubricModal.description}
                  </p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 pt-2">
            <p className="text-xs uppercase tracking-wide font-semibold text-gray-500">
              Levels available for this SLO are highlighted in green.
            </p>
            {(rubricModal?.rubric || []).filter(r => r.level !== undefined).map((r) => {
              const available = rubricModal?.skill_level !== undefined && r.level <= rubricModal.skill_level;
              return (
                <div
                  key={r.level}
                  className={`rounded-lg border p-3 flex gap-3 ${
                    available ? 'bg-emerald-50 border-emerald-300' : 'bg-gray-50 border-gray-200 opacity-60'
                  }`}
                  data-testid={`rubric-level-${r.level}`}
                >
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                    available ? 'bg-emerald-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    {r.level}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-800">
                      {r.label}
                      {!available && <span className="ml-2 text-xs font-normal text-gray-500">(above this SLO&apos;s skill level)</span>}
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5 leading-snug">{r.description}</p>
                  </div>
                </div>
              );
            })}
            {(rubricModal?.rubric || []).filter(r => r.note).map((r, i) => (
              <p key={i} className="text-xs text-gray-500 italic pt-2 border-t mt-2">
                Note: {r.note}
              </p>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Draft Confirmation Dialog */}
      <AlertDialog open={!!resetModalData} onOpenChange={(open) => { if (!open) { setResetModalData(null); setResetReason(''); } }}>
        <AlertDialogContent data-testid="reset-draft-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-red-500" />
              Reset Draft Submission
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                You are about to reset <strong>{resetModalData?.studentName}</strong>&apos;s submission for <strong>{resetModalData?.draftTitle}</strong>.
                This will delete their current submission and allow them to resubmit.
              </p>
              <div className="space-y-2 pt-2">
                <label className="text-sm font-medium text-gray-700">Reason for reset:</label>
                <Select value={resetReason} onValueChange={setResetReason}>
                  <SelectTrigger className="w-full" data-testid="reset-reason-select">
                    <SelectValue placeholder="Select a reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Absent - sick leave">Absent - sick leave</SelectItem>
                    <SelectItem value="Absent - traditional/cultural obligation">Absent - traditional/cultural obligation</SelectItem>
                    <SelectItem value="Technical error - student mistake">Technical error - student mistake</SelectItem>
                    <SelectItem value="Wrong file submitted">Wrong file submitted</SelectItem>
                    <SelectItem value="Incomplete submission">Incomplete submission</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-draft-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={!resetReason || resettingDraft}
              onClick={handleResetDraft}
              data-testid="reset-draft-confirm">
              {resettingDraft ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}
              Reset Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


// ---- Progress Dashboard Component ----

function ProgressDashboard({ data, tasks }) {
  if (!data || !data.students) return null;

  const { students, summary, total_students } = data;
  const allTasks = data.tasks || tasks;

  return (
    <div className="space-y-6" data-testid="progress-dashboard">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {allTasks.map(task => {
          const taskSummary = summary[task.id];
          if (!taskSummary) return null;
          
          const totalDraftSubmissions = Object.values(taskSummary.drafts).reduce((acc, d) => 
            acc + (d.submitted || 0) + (d.reviewed || 0) + (d.approved || 0), 0
          );
          const totalDraftSlots = Object.keys(taskSummary.drafts).length * total_students;
          const draftPercent = totalDraftSlots > 0 ? Math.round((totalDraftSubmissions / totalDraftSlots) * 100) : 0;
          const finalsSubmitted = (taskSummary.final?.submitted || 0) + (taskSummary.final?.marked || 0);

          return (
            <Card key={task.id} className="border-t-4 border-t-emerald-500" data-testid={`summary-task-${task.task_number}`}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-sm">Task {task.task_number}: {task.title}</h3>
                  <Badge variant="outline">{task.weighting}%</Badge>
                </div>
                
                {/* Draft Progress Bar */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Drafts submitted</span>
                    <span className="font-semibold">{draftPercent}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-emerald-500 h-2.5 rounded-full transition-all" style={{ width: `${draftPercent}%` }} />
                  </div>
                </div>

                {/* Draft breakdown */}
                <div className="space-y-1.5">
                  {task.drafts.map(draft => {
                    const dCounts = taskSummary.drafts[draft.id] || {};
                    const submitted = (dCounts.submitted || 0) + (dCounts.reviewed || 0) + (dCounts.approved || 0);
                    return (
                      <div key={draft.id} className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">Draft {draft.draft_number}</span>
                        <div className="flex items-center gap-1.5">
                          <span className={`font-semibold ${submitted === total_students ? 'text-emerald-600' : 'text-gray-700'}`}>
                            {submitted}/{total_students}
                          </span>
                          {dCounts.approved > 0 && <Badge className="bg-emerald-600 text-[10px] px-1 py-0">{dCounts.approved} approved</Badge>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Final submission */}
                <div className="mt-3 pt-2 border-t flex items-center justify-between text-xs">
                  <span className="font-medium text-gray-700">Final Submissions</span>
                  <span className="font-semibold">{finalsSubmitted}/{total_students}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Status Legend */}
      <div className="flex flex-wrap items-center gap-3 px-1">
        <span className="text-xs font-medium text-gray-500">Status:</span>
        {Object.entries({
          not_started: 'Not Started', in_progress: 'In Progress', submitted: 'Submitted',
          reviewed: 'Reviewed', approved: 'Approved'
        }).map(([key, label]) => (
          <div key={key} className="flex items-center gap-1">
            <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] ${STATUS_COLORS[key]}`}>
              {STATUS_ICONS[key]}
            </span>
            <span className="text-xs text-gray-600">{label}</span>
          </div>
        ))}
      </div>

      {/* Detailed Student Grid */}
      <Card data-testid="progress-grid">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4" /> Student Progress Grid
            <Badge variant="secondary" className="ml-2">{total_students} students</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {total_students === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No Year 13 ICT students found in your school</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="progress-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-semibold text-gray-700 sticky left-0 bg-white min-w-[150px]">Student</th>
                    {allTasks.map(task => (
                      <React.Fragment key={task.id}>
                        {task.drafts.map(draft => (
                          <th key={draft.id} className="text-center py-2 px-1 font-medium text-gray-600 text-xs min-w-[60px]">
                            <div>T{task.task_number}</div>
                            <div className="text-gray-400">D{draft.draft_number}</div>
                          </th>
                        ))}
                        <th className="text-center py-2 px-1 font-medium text-gray-600 text-xs min-w-[60px] border-l">
                          <div>T{task.task_number}</div>
                          <div className="text-gray-400">Final</div>
                        </th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, idx) => (
                    <tr key={student.id} className={idx % 2 === 0 ? 'bg-gray-50/50' : ''} data-testid={`progress-row-${student.id}`}>
                      <td className="py-2 px-2 font-medium text-gray-800 sticky left-0 bg-inherit truncate max-w-[150px]" title={student.name}>
                        {student.name}
                      </td>
                      {allTasks.map(task => {
                        const taskProgress = student.tasks[task.id];
                        return (
                          <React.Fragment key={task.id}>
                            {task.drafts.map(draft => {
                              const draftStatus = taskProgress?.drafts?.[draft.id]?.status || 'not_started';
                              return (
                                <td key={draft.id} className="text-center py-2 px-1">
                                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-[10px] font-medium ${STATUS_COLORS[draftStatus]}`}
                                    title={`${student.name} - T${task.task_number} Draft ${draft.draft_number}: ${draftStatus}`}>
                                    {STATUS_ICONS[draftStatus]}
                                  </span>
                                </td>
                              );
                            })}
                            <td className="text-center py-2 px-1 border-l">
                              {taskProgress?.final ? (
                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-[10px] font-medium ${STATUS_COLORS[taskProgress.final.status]}`}
                                  title={`${student.name} - T${task.task_number} Final: ${taskProgress.final.status}${taskProgress.final.marks ? ` (${taskProgress.final.marks}/${taskProgress.final.total_marks})` : ''}`}>
                                  {taskProgress.final.marks !== null && taskProgress.final.marks !== undefined
                                    ? <span className="text-[10px] font-bold">{taskProgress.final.marks}</span>
                                    : STATUS_ICONS[taskProgress.final.status]}
                                </span>
                              ) : (
                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-[10px] font-medium ${STATUS_COLORS.not_submitted}`}
                                  title={`${student.name} - T${task.task_number} Final: Not submitted`}>
                                  {STATUS_ICONS.not_submitted}
                                </span>
                              )}
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ---- Per-SLO Scoring Panel ----
// Embedded inside each student submission card. Lets the teacher tap a level
// (0..skill_level) for each SLO covered by the draft; auto-saves to backend.
function SloScoringPanel({ submissionId, submissionKind, studentName, sloCodes, taskLearningOutcomes, rubric, taskTitle }) {
  const [scores, setScores] = useState({});  // {slo_code: level}
  const [savingCode, setSavingCode] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [rubricFor, setRubricFor] = useState(null);  // { slo_code, lo, levelCap }

  // Resolve codes -> {code, skill_level, description}
  const slos = (sloCodes || []).map(c => {
    const code = typeof c === 'object' ? c.slo_code : c;
    const full = taskLearningOutcomes.find(x => x.slo_code === code);
    return {
      code,
      description: full?.description || '',
      skill_level: full?.skill_level ?? 4,
    };
  });

  useEffect(() => {
    if (!expanded || loaded) return;
    (async () => {
      try {
        const res = await fetch(`/api/ia-programs/submissions/${submissionKind}/${submissionId}/slo-scores`);
        if (res.ok) {
          const data = await res.json();
          const map = {};
          for (const s of (data.scores || [])) map[s.slo_code] = s.level;
          setScores(map);
        }
      } catch (e) { /* ignore */ }
      setLoaded(true);
    })();
  }, [expanded, loaded, submissionKind, submissionId]);

  const saveLevel = async (code, level) => {
    setSavingCode(code);
    setScores(prev => ({ ...prev, [code]: level }));  // optimistic
    try {
      const res = await fetch(
        `/api/ia-programs/submissions/${submissionKind}/${submissionId}/slo-scores/${encodeURIComponent(code)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ level }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Failed to save ${code}`);
        setScores(prev => { const n = { ...prev }; delete n[code]; return n; });
      }
    } catch {
      toast.error(`Failed to save ${code}`);
    } finally {
      setSavingCode(null);
    }
  };

  const clearLevel = async (code) => {
    setSavingCode(code);
    try {
      await fetch(
        `/api/ia-programs/submissions/${submissionKind}/${submissionId}/slo-scores/${encodeURIComponent(code)}`,
        { method: 'DELETE' }
      );
      setScores(prev => { const n = { ...prev }; delete n[code]; return n; });
    } finally {
      setSavingCode(null);
    }
  };

  // Aggregate
  const totalPossible = slos.reduce((acc, s) => acc + s.skill_level, 0);
  const totalAwarded = slos.reduce((acc, s) => acc + (scores[s.code] !== undefined ? scores[s.code] : 0), 0);
  const scoredCount = slos.filter(s => scores[s.code] !== undefined).length;
  const percent = totalPossible > 0 ? Math.round((totalAwarded / totalPossible) * 100) : 0;

  if (slos.length === 0) return null;

  return (
    <div className="border border-emerald-200 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 overflow-hidden" data-testid={`slo-scoring-${submissionId}`}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-emerald-100/40 transition-colors"
        data-testid={`slo-scoring-toggle-${submissionId}`}
      >
        <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wide flex items-center gap-2">
          <Shield className="w-3.5 h-3.5" />
          Per-SLO Scoring · {scoredCount}/{slos.length} scored
        </span>
        <span className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs bg-white border-emerald-300 text-emerald-700">
            {totalAwarded}/{totalPossible} · {percent}%
          </Badge>
          {expanded ? <ChevronUp className="w-4 h-4 text-emerald-700" /> : <ChevronDown className="w-4 h-4 text-emerald-700" />}
        </span>
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-2 bg-white/50">
          {!loaded ? (
            <div className="flex items-center justify-center py-4 text-emerald-700">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading scores...
            </div>
          ) : (
            slos.map(s => {
              const cap = s.skill_level;
              const current = scores[s.code];
              return (
                <div key={s.code} className="bg-white rounded-lg border border-emerald-100 p-2.5 flex items-center gap-2.5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button
                        type="button"
                        className="text-[11px] font-mono text-emerald-700 hover:underline"
                        onClick={() => setRubricFor({ code: s.code, description: s.description, skill_level: cap })}
                      >
                        {s.code}
                      </button>
                      <Badge variant="secondary" className="text-[10px] h-4 px-1">SL {cap}</Badge>
                    </div>
                    <p className="text-[11px] text-gray-600 leading-snug truncate" title={s.description}>{s.description}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {[0, 1, 2, 3, 4].map(lvl => {
                      const disabled = lvl > cap || savingCode === s.code;
                      const selected = current === lvl;
                      return (
                        <button
                          key={lvl}
                          type="button"
                          disabled={disabled}
                          onClick={() => saveLevel(s.code, lvl)}
                          data-testid={`slo-level-${s.code}-${lvl}`}
                          className={`w-7 h-7 rounded-md text-xs font-bold transition-all ${
                            selected
                              ? 'bg-emerald-600 text-white shadow-md scale-110'
                              : disabled
                                ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                : 'bg-gray-100 text-gray-600 hover:bg-emerald-100 hover:text-emerald-700'
                          }`}
                        >
                          {lvl}
                        </button>
                      );
                    })}
                    {current !== undefined && (
                      <button
                        type="button"
                        onClick={() => clearLevel(s.code)}
                        title="Clear score"
                        className="ml-1 w-6 h-6 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex items-center justify-center"
                        data-testid={`slo-clear-${s.code}`}
                      >
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Per-SLO Rubric mini-modal (opens when teacher clicks the SLO code) */}
      <Dialog open={!!rubricFor} onOpenChange={(open) => { if (!open) setRubricFor(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="inline-rubric-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Shield className="w-4 h-4 text-emerald-600" /> Scoring Rubric — {rubricFor?.code}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-1.5 pt-1">
                <p className="text-[11px] text-gray-500">{taskTitle} · {studentName}</p>
                {rubricFor?.skill_level !== undefined && (
                  <Badge variant="secondary" className="text-xs">Skill Level {rubricFor.skill_level} (max: {rubricFor.skill_level})</Badge>
                )}
                <p className="text-sm text-gray-700 pt-1"><span className="font-semibold">LO: </span>{rubricFor?.description}</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-1">
            {(rubric || []).filter(r => r.level !== undefined).map(r => {
              const avail = rubricFor?.skill_level !== undefined && r.level <= rubricFor.skill_level;
              return (
                <div key={r.level} className={`rounded-lg border p-2.5 flex gap-2.5 ${avail ? 'bg-emerald-50 border-emerald-300' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${avail ? 'bg-emerald-600 text-white' : 'bg-gray-300 text-gray-600'}`}>{r.level}</div>
                  <div>
                    <div className="font-semibold text-xs text-gray-800">{r.label}</div>
                    <p className="text-[11px] text-gray-600 leading-snug">{r.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
